import { CircularProgress } from "@material-ui/core";
import { useWatch } from "react-hook-form";
import money from "../../utilities/money";

export default function PreviewUpdatesSidebar({ isLoadingPreview, previewInfo }) {
  const [companyName, billing, seats, enrichment, dedicatedCsm] = useWatch({
    name: ["companyName", "billing", "seats", "enrichment", "dedicatedCsm"],
  });
  const prorationEnabled = billing?.prorationEnabled;

  const seatsPreview = previewInfo?.seats || {};
  const enrichmentPreview = previewInfo?.enrichment || {};
  const dedicatedPreview = previewInfo?.dedicatedCsm || {};
  const totalProratedAmount = previewInfo?.totalProratedAmount ?? 0;
  const totalAmount = previewInfo?.totalAmount ?? 0;
  const oneYearTotalAmount = previewInfo?.oneYearTotalAmount ?? 0;

  const formatCents = (amt) => money.formatDollars(money.centsToDollars(amt));

  const getIntervalLabel = () => {
    const opt = billing?.intervalOption;
    switch (opt) {
      case "monthly":
        return "Monthly";
      case "quarterly":
        return "Quarterly (every 3 months)";
      case "semiAnnually":
        return "Semi-annually (every 6 months)";
      case "yearly":
        return "Yearly";
      case "2year":
        return "2 year (every 2 years)";
      case "3year":
        return "3 year (every 3 years)";
      default:
        return `${billing?.interval || "year"} × ${billing?.intervalCount || 1}`;
    }
  };

  return (
    <aside>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 min-h-96">
        {isLoadingPreview ? (
          <div className="flex items-center justify-center min-h-96">
            <CircularProgress style={{ color: "gray" }} />
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold text-gray-900">Summary</h3>
              <span className="inline-flex items-center px-2. 5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                Preview
              </span>
            </div>

            <div className="space-y-4">
              {/* Company Section */}
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Company
                </div>
                <div className="text-sm font-medium text-gray-900">{companyName || "—"}</div>
              </div>

              {/* Billing Section */}
              <div className="border-t border-gray-200 pt-4">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Billing
                </div>
                {(() => (
                  <>
                    <div className="text-sm font-medium text-gray-900">{getIntervalLabel()}</div>
                    {billing?.generateInvoice && (
                      <div className="text-xs text-amber-600 mt-1 font-medium">
                        ⚠ One-off invoice will be generated
                      </div>
                    )}
                  </>
                ))()}
              </div>

              {/* Seats Section */}
              <div className="border-t border-gray-200 pt-4">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Seats
                </div>

                <div className="text-sm text-gray-700">
                  <div>
                    Existing:{" "}
                    <span className="font-medium">{seatsPreview.existingNumberSeats ?? "—"}</span> ×{" "}
                    {formatCents(seatsPreview.existingUnitAmount)} ={" "}
                    <span className="font-medium">
                      {formatCents(seatsPreview.existingTotalAmount)}
                    </span>
                  </div>
                  <div className="mt-1">
                    New: <span className="font-medium">{seatsPreview.newNumberSeats ?? "—"}</span> ×{" "}
                    {formatCents(seatsPreview.newUnitAmount)} ={" "}
                    <span className="font-medium">{formatCents(seatsPreview.newTotalAmount)}</span>
                  </div>
                  <div className="mt-1">
                    Additional seats: {seatsPreview.additionalSeats ?? "—"}
                  </div>
                  <div className="mt-1">Difference: {formatCents(seatsPreview.difference)}</div>
                  {billing?.generateInvoice && prorationEnabled && (
                    <>
                      <div className="mt-1">
                        Proration factor: {Number(seatsPreview.prorationFactor).toFixed(2) ?? "—"}
                      </div>
                      <div className="mt-1 font-semibold">
                        Prorated total: {formatCents(seatsPreview.proratedTotalAmount)}
                      </div>
                    </>
                  )}
                </div>

                <div className="text-xs text-gray-500 mt-2">
                  {(() => {
                    if (seats.hasCustomPricing) {
                      return (
                        <>
                          Price per seat:{" "}
                          {formatCents(
                            seatsPreview.newUnitAmount ?? seatsPreview.existingUnitAmount,
                          )}
                        </>
                      );
                    }
                    return <>Price per seat: (server default)</>;
                  })()}
                </div>
              </div>

              {/* Enrichment Section */}
              {enrichment.enabled && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Enrichment
                  </div>

                  <div className="text-sm text-gray-700">
                    <div>
                      Existing:{" "}
                      <span className="font-medium">
                        {enrichmentPreview.existingNumberCredits
                          ?? enrichmentPreview.existingNumberCredits
                          ?? "—"}
                      </span>{" "}
                      × {formatCents(enrichmentPreview.existingUnitAmount)} ={" "}
                      <span className="font-medium">
                        {formatCents(enrichmentPreview.existingTotalAmount)}
                      </span>
                    </div>
                    <div className="mt-1">
                      New:{" "}
                      <span className="font-medium">
                        {enrichmentPreview.newNumberCredits
                          ?? enrichmentPreview.newNumberCredits
                          ?? "—"}
                      </span>{" "}
                      × {formatCents(enrichmentPreview.newUnitAmount)} ={" "}
                      <span className="font-medium">
                        {formatCents(enrichmentPreview.newTotalAmount)}
                      </span>
                    </div>
                    <div className="mt-1">
                      Additional credits: {enrichmentPreview.additionalCredits ?? "—"}
                    </div>
                    <div className="mt-1">
                      Difference: {formatCents(enrichmentPreview.difference)}
                    </div>
                    {billing?.generateInvoice && prorationEnabled && (
                      <>
                        <div className="mt-1">
                          Proration factor:{" "}
                          {Number(enrichmentPreview.prorationFactor).toFixed(2) ?? "—"}
                        </div>
                        <div className="mt-1 font-semibold">
                          Prorated total: {formatCents(enrichmentPreview.proratedTotalAmount)}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="text-xs text-gray-500 mt-2">
                    {(() => {
                      if (enrichment.hasCustomPricing) {
                        return (
                          <>
                            Price per credit:{" "}
                            {formatCents(
                              enrichmentPreview.newUnitAmount
                              ?? enrichmentPreview.existingUnitAmount,
                            )}
                          </>
                        );
                      }
                      return <>Price per credit: (server default)</>;
                    })()}
                  </div>

                  <div className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 text-xs font-medium text-gray-700 mt-2">
                    {enrichment.level}
                  </div>
                </div>
              )}

              {/* Enrichment Features Section */}
              {enrichment.enabled && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                    Features
                  </div>
                  <div className="flex flex-col gap-1. 5">
                    {(() => {
                      const CheckIcon = () => (
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      );

                      const FeatureItem = ({ label }) => (
                        <div className="flex items-center gap-2 text-xs text-gray-700">
                          <CheckIcon />
                          {label}
                        </div>
                      );

                      return (
                        <>
                          {enrichment.options.hasListEnrichment && (
                            <FeatureItem label="List enrichment" />
                          )}
                          {enrichment.options.hasFreeListEnrichment && (
                            <FeatureItem label="Free list enrichment" />
                          )}
                          {enrichment.options.hasEventIntelligence && (
                            <FeatureItem label="Event intelligence" />
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Dedicated CSM Section */}
              {dedicatedCsm.enabled && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Dedicated CSM
                  </div>

                  <div className="text-sm text-gray-700">
                    <div>
                      Existing:{" "}
                      <span className="font-medium">
                        {dedicatedPreview.existingQuantity ?? "—"}
                      </span>{" "}
                      × {formatCents(dedicatedPreview.existingUnitAmount)} ={" "}
                      <span className="font-medium">
                        {formatCents(dedicatedPreview.existingTotalAmount)}
                      </span>
                    </div>
                    <div className="mt-1">
                      New:{" "}
                      <span className="font-medium">{dedicatedPreview.newQuantity ?? "—"}</span> ×{" "}
                      {formatCents(dedicatedPreview.newUnitAmount)} ={" "}
                      <span className="font-medium">
                        {(() => {
                          if (dedicatedPreview.newQuantity != null) {
                            return formatCents(
                              dedicatedPreview.newUnitAmount * dedicatedPreview.newQuantity,
                            );
                          }
                          return formatCents(dedicatedPreview.newTotalAmount);
                        })()}
                      </span>
                    </div>
                    <div className="mt-1">
                      Additional quantity: {dedicatedPreview.additionalQuantity ?? "—"}
                    </div>
                    <div className="mt-1">
                      Difference: {formatCents(dedicatedPreview.difference)}
                    </div>
                    {billing?.generateInvoice && prorationEnabled && (
                      <>
                        <div className="mt-1">
                          Proration factor:{" "}
                          {Number(dedicatedPreview.prorationFactor).toFixed(2) ?? "—"}
                        </div>
                        <div className="mt-1 font-semibold">
                          Prorated total: {formatCents(dedicatedPreview.proratedTotalAmount)}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Totals Section */}
              <div className="border-t border-gray-200 pt-4">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                  Totals
                </div>
                <div className="mt-2">
                  {(() => {
                    if (billing?.generateInvoice) {
                      if (prorationEnabled) {
                        return (
                          <div className="flex flex-col gap-1.5">
                            <div className="flex justify-between text-sm text-gray-600">
                              <div>Seats (prorated)</div>
                              <div>
                                {formatCents(
                                  seatsPreview.proratedTotalAmount
                                  ?? seatsPreview.newTotalAmount
                                  ?? seatsPreview.existingTotalAmount,
                                )}
                              </div>
                            </div>
                            {enrichment.enabled && (
                              <div className="flex justify-between text-sm text-gray-600">
                                <div>Enrichment (prorated)</div>
                                <div>
                                  {formatCents(
                                    enrichmentPreview.proratedTotalAmount
                                    ?? enrichmentPreview.newTotalAmount
                                    ?? enrichmentPreview.existingTotalAmount,
                                  )}
                                </div>
                              </div>
                            )}
                            {dedicatedCsm.enabled && (
                              <div className="flex justify-between text-sm text-gray-600">
                                <div>Dedicated CSM (prorated)</div>
                                <div>
                                  {formatCents(
                                    dedicatedPreview.proratedTotalAmount
                                    ?? dedicatedPreview.newTotalAmount
                                    ?? dedicatedPreview.existingTotalAmount,
                                  )}
                                </div>
                              </div>
                            )}
                            <div className="flex justify-between text-sm text-gray-600">
                              <div>Subscription Increase / cycle</div>
                              <div>{formatCents(totalAmount)}</div>
                            </div>
                            <div className="flex justify-between text-sm text-gray-600">
                              <div>Est. yearly Subscription Increase</div>
                              <div>{formatCents(oneYearTotalAmount)}</div>
                            </div>
                            <div className="flex justify-between text-base font-semibold text-gray-900">
                              <div>Invoice prorated</div>
                              <div>{formatCents(totalProratedAmount)}</div>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div className="flex flex-col gap-1.5">
                          <div className="flex justify-between text-sm text-gray-600 mt-1">
                            <div>Est. yearly Subscription Increase</div>
                            <div>{formatCents(oneYearTotalAmount)}</div>
                          </div>

                          <div className="flex justify-between text-base font-semibold text-gray-900">
                            <div>Invoice</div>
                            <div>{formatCents(totalAmount)}</div>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div className="flex flex-col gap-1.5">
                        <div className="flex justify-between text-base font-semibold text-gray-900">
                          <div>Subscription Increase</div>
                          <div>{formatCents(totalAmount)}</div>
                        </div>
                        <div className="flex justify-between text-sm text-gray-600 mt-1">
                          <div>Est. yearly Subscription Increase</div>
                          <div>{formatCents(oneYearTotalAmount)}</div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
